/**
 * WallOutletsGrid — la tabla de tomas de pared en modo EDICIÓN.
 *
 * POR QUÉ EXISTE
 * Dar de alta una remesa de tomas (48 de un edificio, por ejemplo) desde la
 * ficha es media mañana. Aquí la tabla del listado es la hoja de cálculo: se
 * pulsa «Añadir fila» y la fila nueva se rellena sola copiando la anterior,
 * que es como se trabaja de verdad (misma sala, misma sede, misma serie):
 *
 *   · Código      → el siguiente de la serie, conservando los ceros (`T-001` → `T-002`)
 *   · Nombre      → copiado
 *   · Sala/planta → copiados
 *   · Sede        → copiada
 *   · Tipo        → copiado
 *   · Patch panel → el MISMO panel, con el SIGUIENTE puerto libre. Si era el
 *                   último libre, la fila se queda sin puerto (y se avisa).
 *
 * Los desplegables de patch panel solo ofrecen paneles DE LA SEDE de la fila
 * que tienen algún puerto libre, y el de puerto solo los puertos libres (ni
 * tomas ya cableadas ni latiguillos ni huecos sin keystone).
 *
 * Las tomas que ya existen se editan AQUÍ MISMO en los campos de texto (código,
 * nombre, sala, planta y tipo) — cambiar un código rápido no debe obligar a
 * abrir la ficha. Sede, patch panel y puerto siguen siendo de la ficha: mover
 * un cable tiene validaciones que aquí no pintan. Los cambios se guardan con
 * «Guardar N cambios» y la fila se marca con el acento mientras esté sucia.
 *
 * Al guardar se usa el alta masiva del backend, que valida fila a fila y
 * devuelve los fallos con su motivo: las filas con problema se quedan en la
 * tabla marcadas en rojo y el resto se crea igual.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { OUTLET_TYPES } from '../config/outletTypes';
import {
  etiquetaPuerto,
  puertoPanelLibre,
  siguienteCodigo,
  siguientePuertoLibre,
} from '../utils/wallOutletGrid';
import { buildLocationMap, locationPath } from '../utils/locationTree';

let secuencia = 0;
const nuevaClave = () => {
  secuencia += 1;
  return `nueva-${secuencia}`;
};

const FILA_VACIA = {
  code: '',
  name: '',
  room: '',
  floor: '',
  location_id: '',
  outlet_type: 'rj45',
  panel_id: '',
  panel_port_id: '',
};

/** Fila a partir de una toma existente (para continuar la serie). */
function baseDesdeToma(toma) {
  return {
    code: toma.code || '',
    name: toma.name || '',
    room: toma.room || '',
    floor: toma.floor || '',
    location_id: toma.location_id ? String(toma.location_id) : '',
    outlet_type: toma.outlet_type || 'rj45',
    panel_id: toma.panel_id ? String(toma.panel_id) : '',
    panel_port_id: toma.panel_port_id ? String(toma.panel_port_id) : '',
  };
}

function WallOutletsGrid({
  companyId,
  locations = [],
  rows = [],
  defaultLocationId = '',
  onOpenOutlet,
  onCreated,
  onUpdated,
}) {
  const [panels, setPanels] = useState([]);
  const [puertos, setPuertos] = useState({});          // id del panel → puertos
  const [nuevas, setNuevas] = useState([]);
  // Cambios sin guardar de las tomas que YA existen: { idToma: { campo: valor } }
  const [ediciones, setEdiciones] = useState({});
  const [erroresEdicion, setErroresEdicion] = useState({}); // idToma → motivo
  const [fallos, setFallos] = useState({});            // clave de fila → motivo
  const [saving, setSaving] = useState(false);
  const [cargandoSede, setCargandoSede] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  // Modo «hoja de cálculo»: ocupa toda la pantalla y el teclado manda.
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  // Clave de la fila a la que hay que llevar el foco (y el scroll) al crearla.
  const [claveEnfocar, setClaveEnfocar] = useState(null);
  const contenedorRef = useRef(null);
  // Espejo de `nuevas` para leer el valor actual dentro de promesas sin esperar
  // a que el estado se propague (añadir fila tiene que ser instantáneo).
  const nuevasRef = useRef(nuevas);
  nuevasRef.current = nuevas;

  // Nombre completo de la sede («Edificio A › Planta 1»), como en el filtro.
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const nombreSede = useCallback(
    (id) => (id ? locationPath(mapaUbicaciones, id) : '') || '',
    [mapaUbicaciones],
  );

  useEffect(() => {
    if (!companyId) return;
    api.getElements(companyId, { element_type: 'panel' })
      .then((r) => setPanels(r.data || []))
      .catch(() => setPanels([]));
  }, [companyId]);

  /** Puertos de un panel, pedidos una sola vez (caché en estado). */
  const cargarPuertos = useCallback(async (panelId) => {
    if (!panelId) return [];
    if (puertos[panelId]) return puertos[panelId];
    try {
      const r = await api.getPanelPorts(panelId, companyId);
      const lista = r.data || [];
      setPuertos((prev) => ({ ...prev, [panelId]: lista }));
      return lista;
    } catch {
      setPuertos((prev) => ({ ...prev, [panelId]: [] }));
      return [];
    }
  }, [companyId, puertos]);

  /**
   * Carga los puertos de TODOS los paneles de una sede. Hace falta para poder
   * ofrecer solo los que tienen puertos libres; los paneles de una sede son
   * pocos, así que son unas pocas llamadas y quedan cacheadas.
   */
  const precargarSede = useCallback(async (sedeId) => {
    if (!sedeId) return;
    const deSede = panels.filter((p) => String(p.location_id || '') === String(sedeId));
    const pendientes = deSede.filter((p) => !puertos[p.id]);
    if (!pendientes.length) return;
    setCargandoSede(true);
    try {
      const pares = await Promise.all(pendientes.map(async (panel) => {
        const r = await api.getPanelPorts(panel.id, companyId).catch(() => ({ data: [] }));
        return [panel.id, r.data || []];
      }));
      setPuertos((prev) => {
        const siguiente = { ...prev };
        pares.forEach(([id, lista]) => { siguiente[id] = lista; });
        return siguiente;
      });
    } finally {
      setCargandoSede(false);
    }
  }, [companyId, panels, puertos]);

  /** Última fila de la que copiar: la pendiente más reciente o la última visible. */
  const filaBase = useCallback(() => {
    if (nuevas.length) return nuevas[nuevas.length - 1];
    if (rows.length) return baseDesdeToma(rows[rows.length - 1]);
    return { ...FILA_VACIA, location_id: defaultLocationId || '' };
  }, [nuevas, rows, defaultLocationId]);

  /**
   * Añade una fila AL INSTANTE: la fila aparece y el foco entra en el código
   * sin esperar a la red. Los puertos del panel se resuelven después y la fila
   * se rellena sola en cuanto llegan. Antes se esperaba a `getPanelPorts` y en
   * una tabla larga eso se notaba (y la fila nacía fuera de la vista).
   */
  const anadirFila = useCallback(() => {
    const base = filaBase();
    const fila = {
      clave: nuevaClave(),
      ...FILA_VACIA,
      code: siguienteCodigo(base.code),
      name: base.name,
      room: base.room,
      floor: base.floor,
      location_id: base.location_id || defaultLocationId || '',
      outlet_type: base.outlet_type,
      panel_id: base.panel_id,
      panel_port_id: '',
    };

    setNuevas((prev) => [...prev, fila]);
    setClaveEnfocar(fila.clave);
    setFallos({});
    setError('');
    if (fila.location_id) precargarSede(fila.location_id);

    if (!base.panel_id) return;
    cargarPuertos(base.panel_id).then((lista) => {
      const usados = new Set(
        nuevasRef.current
          .filter((f) => f.clave !== fila.clave && f.panel_port_id)
          .map((f) => String(f.panel_port_id)),
      );
      const anterior = lista.find((p) => String(p.id) === String(base.panel_port_id));
      const siguiente = siguientePuertoLibre(
        lista,
        anterior ? Number(anterior.port_number) : null,
        usados,
      );
      if (!siguiente) {
        setAviso(
          `Sin puertos libres en ${panels.find((p) => String(p.id) === base.panel_id)?.name || 'el patch panel'}: la fila se queda sin puerto de panel.`
        );
        return;
      }
      setNuevas((prev) => prev.map((f) => (
        f.clave === fila.clave && !f.panel_port_id
          ? { ...f, panel_port_id: String(siguiente.id) }
          : f
      )));
    });
  }, [filaBase, cargarPuertos, defaultLocationId, panels, precargarSede]);

  /**
   * Deja la fila a la vista desplazando SOLO el contenedor de la tabla. Con
   * `scrollIntoView` el navegador también movía horizontalmente ancestros con
   * `overflow: hidden` y la página entera se iba de lado.
   */
  const asegurarVisible = (el) => {
    const wrap = el?.closest('.list-table-wrap');
    const tr = el?.closest('tr');
    if (!wrap || !tr) return;
    const fila = tr.getBoundingClientRect();
    const caja = wrap.getBoundingClientRect();
    const margen = 6;
    if (fila.bottom > caja.bottom - margen) wrap.scrollTop += fila.bottom - caja.bottom + margen;
    else if (fila.top < caja.top + margen) wrap.scrollTop -= caja.top - fila.top + margen;
  };

  /* Lleva el foco (y el scroll) a la fila recién añadida. Sin esto, en una
     tabla larga la fila nueva nace por debajo del pliegue y parece que no ha
     pasado nada. */
  useEffect(() => {
    if (!claveEnfocar) return;
    const tr = contenedorRef.current?.querySelector(`tr[data-clave="${claveEnfocar}"]`);
    const campo = tr?.querySelector('input[aria-label="Código"]');
    if (campo) {
      campo.focus({ preventScroll: true });
      // El código viene relleno: seleccionado, teclear lo sustituye de golpe.
      campo.select?.();
      asegurarVisible(campo);
    }
    setClaveEnfocar(null);
  }, [claveEnfocar]);

  /** Foco a la misma columna de la fila de arriba/abajo (como una hoja). */
  const moverFoco = (el, delta) => {
    const tr = el.closest('tr');
    if (!tr) return false;
    const indice = [...tr.children].indexOf(el.closest('td'));
    const hermanas = [...tr.parentElement.querySelectorAll('tr.list-row--new')];
    const destino = hermanas[hermanas.indexOf(tr) + delta];
    const campo = destino?.children[indice]?.querySelector('input, select');
    if (!campo) return false;
    campo.focus({ preventScroll: true });
    asegurarVisible(campo);
    return true;
  };

  /**
   * Teclado, para no soltar el teclado en toda la remesa:
   *   Enter           fila nueva si es la última; si no, baja una celda
   *   Ctrl/⌘ + Enter  fila nueva desde cualquier celda
   *   ↑ / ↓           misma columna, fila anterior/siguiente
   *   Esc             sale del modo pantalla completa
   */
  const alPulsarTecla = (e) => {
    if (e.key === 'Escape' && pantallaCompleta) {
      e.stopPropagation();
      setPantallaCompleta(false);
      return;
    }
    const esInput = e.target.tagName === 'INPUT';
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      anadirFila();
      return;
    }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && esInput) {
      if (moverFoco(e.target, e.key === 'ArrowDown' ? 1 : -1)) e.preventDefault();
      return;
    }
    if (e.key === 'Enter' && esInput) {
      const tr = e.target.closest('tr');
      // En una toma existente, Enter no crea nada: se está retocando un dato.
      if (!tr?.classList.contains('list-row--new')) return;
      e.preventDefault();
      const filas = [...tr.parentElement.children];
      const esUltima = filas[filas.length - 1] === tr;
      if (esUltima || !moverFoco(e.target, 1)) anadirFila();
    }
  };

  const actualizar = (clave, campo, valor) => {
    setNuevas((prev) => prev.map((fila) => {
      if (fila.clave !== clave) return fila;
      const siguiente = { ...fila, [campo]: valor };
      // Cambiar de sede invalida el panel (un panel es de una sede).
      if (campo === 'location_id') { siguiente.panel_id = ''; siguiente.panel_port_id = ''; }
      // Cambiar de panel invalida el puerto.
      if (campo === 'panel_id') { siguiente.panel_port_id = ''; }
      return siguiente;
    }));
    if (campo === 'location_id') precargarSede(valor);
    if (campo === 'panel_id') cargarPuertos(valor);
    setFallos((prev) => {
      if (!prev[clave]) return prev;
      const siguiente = { ...prev };
      delete siguiente[clave];
      return siguiente;
    });
    setError('');
  };

  const borrarFila = (clave) => setNuevas((prev) => prev.filter((f) => f.clave !== clave));

  /* ── Edición en línea de las tomas que ya existen ─────────────────────────
     Se pueden retocar los campos «de texto» (código, nombre, sala, planta y
     tipo). Sede, patch panel y puerto se dejan como están a propósito: cambiarlos
     mueve el cableado y eso se hace en la ficha, que valida el puerto. */

  /** Valor que se pinta en una celda: el cambio sin guardar o el de la toma. */
  const valorToma = (toma, campo) => {
    const cambio = ediciones[toma.id];
    if (cambio && campo in cambio) return cambio[campo];
    if (campo === 'outlet_type') return toma.outlet_type || 'rj45';
    return toma[campo] ?? '';
  };

  /** Anota un cambio; si vuelve al valor original deja de estar «sucio». */
  const cambiarToma = (toma, campo, valor) => {
    const original = campo === 'outlet_type' ? (toma.outlet_type || 'rj45') : (toma[campo] ?? '');
    setEdiciones((prev) => {
      const delToma = { ...(prev[toma.id] || {}) };
      if (String(valor) === String(original)) delete delToma[campo];
      else delToma[campo] = valor;
      const siguiente = { ...prev };
      if (Object.keys(delToma).length) siguiente[toma.id] = delToma;
      else delete siguiente[toma.id];
      return siguiente;
    });
    setErroresEdicion((prev) => {
      if (!prev[toma.id]) return prev;
      const siguiente = { ...prev };
      delete siguiente[toma.id];
      return siguiente;
    });
    setError('');
  };

  const cambios = Object.keys(ediciones).length;

  const guardarEdiciones = async () => {
    if (!cambios) return;
    setError('');

    // Códigos: ni vacíos ni repetidos (el endpoint de actualización no lo valida).
    const codigos = new Map();
    rows.forEach((t) => codigos.set(String(t.code || '').trim().toLowerCase(), String(t.id)));
    nuevas.forEach((f) => {
      if (f.code.trim()) codigos.set(f.code.trim().toLowerCase(), `nueva-${f.clave}`);
    });

    const problemas = {};
    Object.entries(ediciones).forEach(([id, cambio]) => {
      if (!('code' in cambio)) return;
      const codigo = String(cambio.code || '').trim();
      if (!codigo) {
        problemas[id] = 'El código no puede quedar vacío';
        return;
      }
      const otro = codigos.get(codigo.toLowerCase());
      if (otro !== undefined && otro !== String(id)) {
        problemas[id] = 'Ese código ya lo usa otra toma';
      }
    });
    if (Object.keys(problemas).length) {
      setErroresEdicion(problemas);
      setError('Revisa las filas marcadas: hay códigos vacíos o repetidos.');
      return;
    }

    setSaving(true);
    const fallados = {};
    let guardados = 0;
    for (const [id, cambio] of Object.entries(ediciones)) {
      try {
        // Se envía solo lo que cambia: el PUT aplica los campos presentes.
        await api.updateWallOutlet(id, { ...cambio }, companyId);
        guardados += 1;
      } catch (e) {
        fallados[id] = e.message || 'No se pudo guardar';
      }
    }
    setSaving(false);

    if (Object.keys(fallados).length) {
      setErroresEdicion(fallados);
      setEdiciones((prev) => Object.fromEntries(
        Object.entries(prev).filter(([id]) => fallados[id]),
      ));
      setError(`${guardados} guardada(s). ${Object.keys(fallados).length} con error: revisa las filas marcadas.`);
      return;
    }
    setEdiciones({});
    setAviso(`${guardados} ${guardados === 1 ? 'toma actualizada' : 'tomas actualizadas'}.`);
    onUpdated?.(guardados);
  };

  const descartar = () => {
    setNuevas([]);
    setEdiciones({});
    setErroresEdicion({});
    setFallos({});
    setError('');
    setAviso('');
  };

  /** Paneles de la sede de la fila que tienen algún puerto libre. */
  const panelesDeFila = (fila) => {
    const deSede = panels.filter((p) => String(p.location_id || '') === String(fila.location_id || ''));
    const conLibres = deSede.filter((panel) => {
      const lista = puertos[panel.id];
      if (!lista) return true;               // aún sin cargar: se ofrece y se filtra al elegirlo
      return lista.some((pt) => puertoPanelLibre(pt));
    });
    // El panel ya elegido se mantiene aunque se haya quedado sin libres.
    const actual = deSede.find((p) => String(p.id) === String(fila.panel_id));
    return actual && !conLibres.includes(actual) ? [actual, ...conLibres] : conLibres;
  };

  /** Puertos que puede elegir una fila: libres y no pillados por otra fila. */
  const puertosDeFila = (fila) => {
    const lista = puertos[fila.panel_id] || [];
    const deOtras = new Set(
      nuevas
        .filter((f) => f.clave !== fila.clave && f.panel_port_id)
        .map((f) => String(f.panel_port_id)),
    );
    return lista
      .filter((p) => puertoPanelLibre(p) || String(p.id) === String(fila.panel_port_id))
      .filter((p) => !deOtras.has(String(p.id)))
      .sort((a, b) => Number(a.port_number) - Number(b.port_number));
  };

  const guardar = async () => {
    setError('');
    const conCodigo = nuevas.filter((f) => f.code.trim());
    if (!conCodigo.length) {
      setError('Escribe al menos un código antes de crear.');
      return;
    }

    // Códigos repetidos dentro de la propia tabla: se marcan antes de enviar.
    const vistos = new Map();
    const repetidos = {};
    conCodigo.forEach((fila) => {
      const clave = fila.code.trim().toLowerCase();
      if (vistos.has(clave)) repetidos[fila.clave] = 'Código repetido en la tabla';
      else vistos.set(clave, fila.clave);
    });
    if (Object.keys(repetidos).length) {
      setFallos(repetidos);
      setError('Revisa las filas marcadas: hay códigos repetidos.');
      return;
    }

    setSaving(true);
    try {
      const items = conCodigo.map((fila) => {
        const panel = panels.find((p) => String(p.id) === String(fila.panel_id));
        return {
          code: fila.code.trim(),
          name: fila.name || null,
          room: fila.room || null,
          floor: fila.floor || null,
          location_id: fila.location_id ? Number(fila.location_id) : null,
          panel_id: fila.panel_id ? Number(fila.panel_id) : null,
          panel_code: panel?.code || null,
          panel_port_id: fila.panel_port_id ? Number(fila.panel_port_id) : null,
          outlet_type: fila.outlet_type || 'rj45',
          status: 'active',
        };
      });

      const respuesta = await api.bulkCreateWallOutlets(items, companyId);
      const creadas = respuesta.count ?? (respuesta.created || []).length;

      const problemas = {};
      (respuesta.failed || []).forEach((fallo) => {
        const fila = conCodigo[fallo.index];
        if (fila) problemas[fila.clave] = fallo.error;
      });

      if (Object.keys(problemas).length) {
        setFallos(problemas);
        // Se dejan solo las que hay que corregir. NO se refresca el listado aquí:
        // al recargar, ListPage desmonta la vista y se perderían estas filas.
        setNuevas((prev) => prev.filter((f) => problemas[f.clave] || !f.code.trim()));
        setError(`${creadas} creada(s). ${Object.keys(problemas).length} con problemas: revisa las filas marcadas.`);
      } else {
        setNuevas([]);
        setAviso(`${creadas} ${creadas === 1 ? 'toma creada' : 'tomas creadas'}.`);
        if (creadas) onCreated?.(creadas);
      }
      // Los puertos que se acaban de ocupar ya no están libres: se tira la caché.
      if (creadas) setPuertos({});
    } catch (e) {
      setError(e.message || 'No se pudieron crear las tomas');
    } finally {
      setSaving(false);
    }
  };

  const vacia = rows.length === 0 && nuevas.length === 0;

  return (
    <div
      ref={contenedorRef}
      className={`list-grid${pantallaCompleta ? ' list-grid--full' : ''}`}
      onKeyDown={alPulsarTecla}
    >
      {/* ── Barra de la tabla editable ── */}
      <div className="list-grid-bar">
        <button type="button" className="list-btn-primary" onClick={anadirFila}>
          <PlusIcon style={{ width: 16, height: 16 }} /> Añadir fila
        </button>
        <button
          type="button"
          className="list-btn-secondary"
          onClick={() => setPantallaCompleta((v) => !v)}
          title={pantallaCompleta ? 'Salir de pantalla completa (Esc)' : 'Ver la tabla a pantalla completa, tipo hoja de cálculo'}
        >
          {pantallaCompleta
            ? <><ArrowsPointingInIcon style={{ width: 16, height: 16 }} /> Salir (Esc)</>
            : <><ArrowsPointingOutIcon style={{ width: 16, height: 16 }} /> Pantalla completa</>}
        </button>

        {pantallaCompleta && (
          <span className="list-grid-title">Tomas de pared · alta rápida</span>
        )}

        <span className="list-hint">
          {nuevas.length > 0 || cambios > 0
            ? [
              nuevas.length ? `${nuevas.length} ${nuevas.length === 1 ? 'fila nueva' : 'filas nuevas'}` : null,
              cambios ? `${cambios} ${cambios === 1 ? 'cambio' : 'cambios'} sin guardar` : null,
              cargandoSede ? 'leyendo puertos…' : null,
            ].filter(Boolean).join(' · ')
            : 'Enter en la última fila nueva añade otra · Tab cambia de campo'}
        </span>
        {aviso && <span className="list-badge list-badge--on">{aviso}</span>}

        <div className="list-grid-actions">
          {nuevas.length > 0 && (
            <button type="button" className="list-btn-primary" onClick={guardar} disabled={saving}>
              {saving ? 'Creando…' : `Crear ${nuevas.length} ${nuevas.length === 1 ? 'toma' : 'tomas'}`}
            </button>
          )}
          {cambios > 0 && (
            <button
              type="button"
              className={`list-btn-${nuevas.length ? 'secondary' : 'primary'}`}
              onClick={guardarEdiciones}
              disabled={saving}
            >
              {saving ? 'Guardando…' : `Guardar ${cambios} ${cambios === 1 ? 'cambio' : 'cambios'}`}
            </button>
          )}
          {(nuevas.length > 0 || cambios > 0) && (
            <button type="button" className="list-btn-secondary" onClick={descartar} disabled={saving}>
              Descartar
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      {vacia && (
        <div className="list-empty">
          <span aria-hidden="true">🧩</span>
          <p className="empty-title">Ninguna toma que mostrar</p>
          <p className="empty-subtitle">
            Pulsa «Añadir fila» para empezar la remesa: la primera fila se rellena desde cero
            y las siguientes copian la anterior.
          </p>
        </div>
      )}

      {!vacia && (
        <div className="list-table-wrap">
          <table className="list-table list-table--editable">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Sala</th>
                <th>Planta</th>
                <th>Sede</th>
                <th>Tipo</th>
                <th>Patch panel</th>
                <th>Puerto</th>
                <th className="list-actions-header" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {/* Tomas existentes: editables los campos de texto. Sede, panel y
                  puerto van en la ficha (mover un cable no es un retoque). */}
              {rows.map((toma) => {
                const cambio = ediciones[toma.id];
                const sucia = Boolean(cambio && Object.keys(cambio).length);
                const err = erroresEdicion[toma.id];
                return (
                  <tr
                    key={`toma-${toma.id}`}
                    className={`list-row--locked${sucia ? ' is-dirty' : ''}${err ? ' is-error' : ''}`}
                  >
                    <td>
                      <input
                        className="list-cell-input"
                        value={valorToma(toma, 'code')}
                        onChange={(e) => cambiarToma(toma, 'code', e.target.value)}
                        aria-label="Código"
                      />
                      {err && <span className="list-cell-error">{err}</span>}
                    </td>
                    <td>
                      <input
                        className="list-cell-input"
                        value={valorToma(toma, 'name')}
                        onChange={(e) => cambiarToma(toma, 'name', e.target.value)}
                        placeholder="—"
                        aria-label="Nombre"
                      />
                    </td>
                    <td>
                      <input
                        className="list-cell-input"
                        value={valorToma(toma, 'room')}
                        onChange={(e) => cambiarToma(toma, 'room', e.target.value)}
                        placeholder="—"
                        aria-label="Sala"
                      />
                    </td>
                    <td>
                      <input
                        className="list-cell-input list-cell-input--short"
                        value={valorToma(toma, 'floor')}
                        onChange={(e) => cambiarToma(toma, 'floor', e.target.value)}
                        placeholder="—"
                        aria-label="Planta"
                      />
                    </td>
                    <td>{nombreSede(toma.location_id) || toma.location_name || <span className="list-hint">—</span>}</td>
                    <td>
                      <select
                        className="list-cell-select"
                        value={valorToma(toma, 'outlet_type')}
                        onChange={(e) => cambiarToma(toma, 'outlet_type', e.target.value)}
                        aria-label="Tipo"
                      >
                        {OUTLET_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>{toma.panel_name || <span className="list-hint">—</span>}</td>
                    <td>{toma.panel_port_label || <span className="list-hint">—</span>}</td>
                    <td>
                      <button
                        type="button"
                        className="list-action-btn"
                        onClick={() => onOpenOutlet?.(toma)}
                        title="Abrir la ficha (sede, patch panel y puerto)"
                      >
                        <ArrowTopRightOnSquareIcon />
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* Filas nuevas: se escriben aquí y se crean de una vez. */}
              {nuevas.map((fila) => (
                <tr
                  key={fila.clave}
                  data-clave={fila.clave}
                  className={`list-row--new${fallos[fila.clave] ? ' is-error' : ''}`}
                >
                  <td>
                    <input
                      className="list-cell-input"
                      value={fila.code}
                      onChange={(e) => actualizar(fila.clave, 'code', e.target.value)}
                      placeholder="T-001"
                      aria-label="Código"
                    />
                    {fallos[fila.clave] && (
                      <span className="list-cell-error">{fallos[fila.clave]}</span>
                    )}
                  </td>
                  <td>
                    <input
                      className="list-cell-input"
                      value={fila.name}
                      onChange={(e) => actualizar(fila.clave, 'name', e.target.value)}
                      placeholder="Mesa 1"
                      aria-label="Nombre"
                    />
                  </td>
                  <td>
                    <input
                      className="list-cell-input"
                      value={fila.room}
                      onChange={(e) => actualizar(fila.clave, 'room', e.target.value)}
                      placeholder="Sala"
                      aria-label="Sala"
                    />
                  </td>
                  <td>
                    <input
                      className="list-cell-input list-cell-input--short"
                      value={fila.floor}
                      onChange={(e) => actualizar(fila.clave, 'floor', e.target.value)}
                      placeholder="Planta"
                      aria-label="Planta"
                    />
                  </td>
                  <td>
                    <select
                      className="list-cell-select"
                      value={fila.location_id}
                      onChange={(e) => actualizar(fila.clave, 'location_id', e.target.value)}
                      aria-label="Sede"
                    >
                      <option value="">— Sede —</option>
                      {locations.map((l) => (
                        <option key={l.id} value={l.id}>{nombreSede(l.id) || l.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-cell-select"
                      value={fila.outlet_type}
                      onChange={(e) => actualizar(fila.clave, 'outlet_type', e.target.value)}
                      aria-label="Tipo"
                    >
                      {OUTLET_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-cell-select"
                      value={fila.panel_id}
                      onChange={(e) => actualizar(fila.clave, 'panel_id', e.target.value)}
                      aria-label="Patch panel"
                      title={fila.location_id
                        ? 'Solo se ofrecen paneles de la sede de la fila con puertos libres'
                        : 'Elige primero la sede de la fila'}
                      disabled={!fila.location_id}
                    >
                      <option value="">— Sin panel —</option>
                      {panelesDeFila(fila).map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="list-cell-select list-cell-select--short"
                      value={fila.panel_port_id}
                      onChange={(e) => actualizar(fila.clave, 'panel_port_id', e.target.value)}
                      aria-label="Puerto del panel"
                      disabled={!fila.panel_id}
                    >
                      <option value="">—</option>
                      {puertosDeFila(fila).map((p) => (
                        <option key={p.id} value={p.id}>
                          {etiquetaPuerto(p)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="list-action-btn list-action-btn--danger"
                      onClick={() => borrarFila(fila.clave)}
                      title="Quitar esta fila"
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default WallOutletsGrid;
